import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, CreditCard, X } from 'lucide-react';
import { bookingsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

/**
 * Persistent banner shown across the app when the authenticated user has a
 * booking in HOLD status with a real deposit pending payment. Displays a
 * live countdown to hold_expires_at and a CTA that jumps to the bookings
 * page so the user can retry Stripe checkout.
 *
 * - Hidden on the bookings page itself (redundant there).
 * - Auto-refreshes every 60 s and on route change.
 * - Dismissible per booking (session-only) via the X button.
 */
export default function PendingHoldBanner() {
  const { isAuthenticated, user } = useAuth();
  const { language } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const [hold, setHold] = useState(null); // { id, business_name, hold_expires_at, deposit_amount }
  const [now, setNow] = useState(() => Date.now());
  const dismissedRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setHold(null);
      return;
    }
    try {
      const res = await bookingsAPI.getMy();
      const bookings = Array.isArray(res.data) ? res.data : [];
      // Pick the soonest-expiring hold with a real deposit
      const candidates = bookings
        .filter((b) => b.status === 'hold' && Number(b.deposit_amount || 0) > 0 && b.hold_expires_at)
        .filter((b) => !dismissedRef.current.has(b.id))
        .filter((b) => new Date(b.hold_expires_at).getTime() > Date.now())
        .sort((a, b) => new Date(a.hold_expires_at) - new Date(b.hold_expires_at));
      setHold(candidates[0] || null);
    } catch {
      // Silent — banner just won't show
    }
  }, [isAuthenticated]);

  // Refresh whenever the route changes or the user logs in/out
  useEffect(() => {
    load();
  }, [load, location.pathname, user?.id]);

  // Polling every 60 s in case a booking is created in another tab
  useEffect(() => {
    if (!isAuthenticated) return;
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [isAuthenticated, load]);

  // Tick the countdown once per second
  useEffect(() => {
    if (!hold) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [hold]);

  if (!hold) return null;
  // Don't render on the bookings page (they can see the card + pay button there).
  if (location.pathname === '/bookings' || location.pathname === '/dashboard/bookings') return null;

  const expiresAt = new Date(hold.hold_expires_at).getTime();
  const diffMs = expiresAt - now;
  if (diffMs <= 0) {
    // Hold already expired — hide immediately.
    return null;
  }
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const dismiss = (e) => {
    e.stopPropagation();
    dismissedRef.current.add(hold.id);
    setHold(null);
  };

  const goPay = () => {
    navigate('/bookings');
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goPay}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goPay()}
      className="md:sticky z-40 w-full cursor-pointer bg-amber-500 text-amber-950 shadow-sm animate-in fade-in slide-in-from-top-2"
      style={{
        // Sit exactly under the navbar on desktop, accounting for the OS
        // status-bar safe area. On mobile the banner is in normal flow.
        top: 'calc(env(safe-area-inset-top) + 64px)',
      }}
      data-testid="pending-hold-banner"
    >
      <div className="container-app flex items-center gap-3 py-2 px-3">
        <Clock className="h-4 w-4 shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-semibold">
            {language === 'es' ? 'Reserva pendiente de pago' : 'Booking awaiting payment'}
          </span>
          <span className="hidden sm:inline"> — </span>
          <span className="block sm:inline text-xs sm:text-sm opacity-90">
            {language === 'es'
              ? `${hold.business_name || 'Negocio'} · Anticipo $${Number(hold.deposit_amount).toFixed(0)} MXN · expira en `
              : `${hold.business_name || 'Business'} · Deposit $${Number(hold.deposit_amount).toFixed(0)} MXN · expires in `}
            <strong className="font-mono tabular-nums">{timeStr}</strong>
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPay(); }}
          className="inline-flex items-center gap-1 rounded-full bg-amber-950 text-amber-50 px-3 py-1 text-xs font-semibold hover:bg-amber-900 active:scale-95 transition"
          data-testid="pending-hold-banner-pay-btn"
        >
          <CreditCard className="h-3.5 w-3.5" />
          {language === 'es' ? 'Pagar' : 'Pay'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={language === 'es' ? 'Ocultar' : 'Dismiss'}
          className="p-1 rounded-full hover:bg-amber-600/40 transition"
          data-testid="pending-hold-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
